import { Droppable } from '@hello-pangea/dnd'

import type { Deal, DealStage } from '@/types/deal'
import { STAGE_DOT, STAGE_LABELS, OPEN_STAGES } from '@/types/deal'
import { sumValue } from '@/features/customers/deal-utils'
import { formatCurrency } from '@/lib/format'
import { DealCard } from './deal-card'

interface KanbanColumnProps {
  stage: DealStage
  deals: Deal[]
  onCardEdit?: (deal: Deal) => void
  onCardMessage?: (deal: Deal) => void
}

export function KanbanColumn({ stage, deals, onCardEdit, onCardMessage }: KanbanColumnProps) {
  const total = sumValue(deals)
  const isClosed = !OPEN_STAGES.includes(stage)

  return (
    <div className="flex min-w-60 flex-1 flex-col gap-3">
      <div className="flex items-center justify-between rounded-md border bg-card/60 px-2 py-2">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${STAGE_DOT[stage]}`} />
          <span className="text-sm font-semibold">{STAGE_LABELS[stage]}</span>
          <span className="text-xs text-muted-foreground">{deals.length}</span>
        </div>
        <span className="text-xs font-medium text-muted-foreground">
          {isClosed && total > 0 ? formatCurrency(total) : ''}
        </span>
      </div>

      {/*
        Droppable uses a render-prop pattern. The provided.innerRef and
        provided.droppableProps MUST be spread on the DOM element that
        contains the draggable children. The provided.placeholder is a
        zero-height spacer that grows during drag to prevent the column
        from collapsing as the source card is lifted out — without it,
        the layout would jump.
      */}
      <Droppable droppableId={stage}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex flex-1 flex-col gap-2 rounded-md p-1 transition-colors ${
              snapshot.isDraggingOver ? 'bg-primary/5 ring-1 ring-inset ring-primary/30' : ''
            }`}
          >
            {deals.map((deal, index) => (
              <DealCard
                key={deal.id}
                deal={deal}
                index={index}
                onEdit={onCardEdit}
                onMessageClick={onCardMessage}
              />
            ))}

            {deals.length === 0 && !snapshot.isDraggingOver && (
              <div className="flex flex-1 items-center justify-center rounded-md border border-dashed py-6 text-center text-xs text-muted-foreground">
                No deals
              </div>
            )}

            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  )
}
